import { useState, type FormEvent } from 'react'
import { Table, useApiQuery, useToast, type Column } from 'grabbitbot-legacy-ui'
import { portalClient } from '../../api'
import type { ServiceAccount } from '../../api/types'
import { failureText } from '../../api/errorMessages'

/**
 * Equivalent of legacy `ServiceAccountListComponent` (routes
 * `settings/service-accounts` + alias `service-accounts`) — manages
 * GrabbitBot's **own** issued service-account login+password (IP-restricted
 * external API access to GrabbitBot itself, NOT an exchange credential —
 * audit §6.4 correction): create, reveal-once, revoke.
 *
 * Port note: the legacy template rendered a free-text `comment` field via
 * `[innerHTML]` through an `nl2br` pipe that calls
 * `DomSanitizer.bypassSecurityTrustHtml()` (audit §10, self-XSS-capable) —
 * NOT ported; React renders any comment as plain text by default, which
 * closes that gap without needing a decision. There is no comment-entry UI
 * in this round either — the contract accepts an optional `comment` on
 * create/modify, but round 1 only carries forward the create form's other
 * field (see below).
 *
 * Contract note (2026-09-27, `legacy-migration/docs/analysis/
 * 2026-09-27-grabbitbot-b1-contract.md` §24-30): the backend generates both
 * `login` and the one-time `secret` itself — a create request only ever
 * carries `roles` + the optional `description` label, there is no
 * client-supplied login. The form below reflects that: an optional
 * "Description" field (the backend defaults it to `unnamed-<timestamp>` when
 * left blank) instead of a login the account never actually gets.
 */
export function ServiceAccountsPage() {
  const { data, loading, error, refetch } = useApiQuery(() => portalClient.searchServiceAccounts(), [])
  const roles = useApiQuery(() => portalClient.searchServiceAccountRoles(), [])
  const { showToast } = useToast()
  const [description, setDescription] = useState('')
  const [selectedRoles, setSelectedRoles] = useState<string[]>([])
  const [revealed, setRevealed] = useState<Record<string, string>>({})
  const [creating, setCreating] = useState(false)

  async function handleCreate(event: FormEvent) {
    event.preventDefault()
    setCreating(true)
    try {
      const created = await portalClient.addServiceAccount({
        roles: selectedRoles,
        description: description.trim() || undefined,
      })
      if (created.secret && created.id) setRevealed((prev) => ({ ...prev, [created.id!]: created.secret! }))
      setDescription('')
      setSelectedRoles([])
      refetch()
      showToast('Service account created', 'success')
    } catch (err) {
      showToast(failureText(err, 'Could not create the service account'), 'error')
    } finally {
      setCreating(false)
    }
  }

  // `GET /serviceAccounts/{id}/pass` is a deliberate, permanent 410 (round-1
  // port plan, B1: "new random bcrypt secrets, `/pass` -> 410 as in the
  // pilot") — the backend never stores a recoverable secret. The legacy
  // frontend assumed a `{ password }` 200 here; against the rewritten
  // backend "Reveal" on any existing account gets a clear message instead of
  // an unhandled rejection.
  async function reveal(id: string) {
    try {
      const { password } = await portalClient.viewServiceAccountPassword(id)
      setRevealed((prev) => ({ ...prev, [id]: password }))
    } catch {
      showToast(
        'Service-account secrets are shown once at creation time and can never be retrieved again — revoke and recreate this account if the secret is lost.',
        'error',
      )
    }
  }

  async function revoke(id: string) {
    await portalClient.revokeServiceAccount(id)
    refetch()
  }

  function toggleRole(role: string) {
    setSelectedRoles((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]))
  }

  const columns: Column<ServiceAccount>[] = [
    { key: 'login', label: 'Login', align: 'left' },
    { key: 'description', label: 'Description', align: 'left' },
    { key: 'roles', label: 'Roles', align: 'left', format: (v) => (Array.isArray(v) ? v.join(', ') : '') },
    // Backend contract (B1 note §24-30, `STATUS_ACTIVE = 0` /
    // `STATUS_REVOKED = 1`): 0 is active, 1 is revoked — inverted from what
    // this page originally assumed (a legacy-Angular-only guess that had it
    // backwards, showing revoked accounts as "Active" and vice versa).
    { key: 'status', label: 'Status', format: (v) => (v === 0 ? 'Active' : 'Revoked') },
    {
      key: 'id',
      label: 'Secret',
      sortable: false,
      align: 'left',
      format: (_v, row) =>
        row.id ? (
          revealed[row.id] ? (
            <code>{revealed[row.id]}</code>
          ) : (
            <button type="button" onClick={() => reveal(row.id!)}>
              Reveal
            </button>
          )
        ) : null,
    },
    {
      key: 'revokedAt',
      label: '',
      sortable: false,
      format: (_v, row) =>
        row.id &&
        !row.revokedAt && (
          <button type="button" onClick={() => revoke(row.id!)}>
            Revoke
          </button>
        ),
    },
  ]

  return (
    <div>
      <h1>Service accounts</h1>

      <form className="portal-form portal-card" onSubmit={handleCreate}>
        <h2>New service account</h2>
        {/* The backend generates both the login and the secret itself
           (B1 contract §24-30) — this optional label is the only thing a
           caller can set on create besides the roles. */}
        <label>
          Description (optional)
          <input value={description} onChange={(e) => setDescription(e.target.value)} />
        </label>
        <div className="portal-actions">
          {(roles.data ?? []).map((role) => (
            <label key={role}>
              <input type="checkbox" checked={selectedRoles.includes(role)} onChange={() => toggleRole(role)} />
              {role}
            </label>
          ))}
        </div>
        <button type="submit" disabled={creating}>
          {creating ? 'Creating…' : 'Create'}
        </button>
      </form>

      {loading && <p>Loading…</p>}
      {error && <p role="alert">{failureText(error, 'Could not load service accounts')}</p>}
      {data && (
        <Table columns={columns} rows={data} rowKey={(row, i) => row.id ?? i} initialSort={{ key: 'login', dir: 'asc' }} />
      )}
    </div>
  )
}
